import React, { useState, useEffect } from "react";
import CanvasJSReact from "@canvasjs/react-charts";
import { VscOpenPreview, VscGraph } from "react-icons/vsc";
import { FaEdit, FaExpandAlt } from "react-icons/fa";
import DrawingOverlay from "./DrawingOverlay";
import GraphModal from "./GraphModal";
import socket from "./socket";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { FaUserSlash, FaUser, FaUserXmark } from "react-icons/fa6";

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

function stringToColor(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  const hue = hash % 360;
  return `hsl(${hue}, 65%, 55%)`;
}

function ClassDashboard({
  expanded,
  setExpanded,
  selectedYear,
  selectedQuarter,
}) {
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [users, setUsers] = useState([]);
  const [currentUserName, setCurrentUserName] = useState("");
  const [classDataFull, setClassDataFull] = useState({
    current: [],
    previous: [],
  }); // display all Class Data
  const [classData, setClassData] = useState({ current: [], previous: [] }); // only display 10 courses
  const [showModal, setShowModal] = useState(false); // Open modal to show all course data
  const [varianceByLevel, setVarianceByLevel] = useState([]); // use for class size variance
  const [currentPage, setCurrentPage] = useState(1); // pagination for Below Target Class Size
  // eslint-disable-next-line no-unused-vars
  const [headcountDataFull, setHeadcountDataFull] = useState([]);
  const itemsPerPage = 7;

  const maxClass =
    classDataFull.current.length > 0
      ? classDataFull.current.reduce((prev, current) =>
          current.y > prev.y ? current : prev
        )
      : null; // Maximum class size from class data Full

  const minClass =
    classDataFull.current.length > 0
      ? classDataFull.current.reduce((prev, current) =>
          current.y < prev.y ? current : prev
        )
      : null; // Minimum class size from class data Full

  const belowTargetClasses =
    classDataFull.current.length > 0
      ? classDataFull.current
          .filter((item) => item.y < 5)
          .sort((a, b) => b.y - a.y) // descending sort
      : [];

  const totalPages = Math.ceil(belowTargetClasses.length / itemsPerPage);

  const paginatedClasses = belowTargetClasses.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const navigate = useNavigate();

  const handleViewTable = () => {
    const currentQuarter = `Q${selectedQuarter} ${selectedYear}`;
    const previousQuarter =
      selectedQuarter === 1
        ? `Q4 ${selectedYear - 1}`
        : `Q${selectedQuarter - 1} ${selectedYear}`;

    navigate("/table", {
      state: {
        selectedQuarters: [currentQuarter, previousQuarter],
        currentData: classDataFull.current,
        previousData: classDataFull.previous,
        title: "Class Size",
      },
    });
  };

  const handleViewStatus = () => {
    const currentQuarter = `Q${selectedQuarter} ${selectedYear}`;
    const previousQuarter =
      selectedQuarter === 1
        ? `Q4 ${selectedYear - 1}`
        : `Q${selectedQuarter - 1} ${selectedYear}`;

    navigate("/detailclassstatus", {
      state: {
        selectedQuarters: [currentQuarter, previousQuarter],
      },
    });
  };

  // Class Size Variance Calculation
  const calculateVarianceByCourseLevel = (currentArr, previousArr) => {
    const varianceMap = {};
    const colors = [
      "#FFA726",
      "#66BB6A",
      "#42A5F5",
      "#AB47BC",
      "#FF7043",
      "#26C6DA",
    ];

    currentArr.forEach((item) => {
      const prevItem = previousArr.find((p) => p.label === item.label);
      const variance = item.y - (prevItem?.y || 0);
      const level = item.course_level || "Uncategorized";
      varianceMap[level] = (varianceMap[level] || 0) + variance;
    });

    previousArr.forEach((item) => {
      const existsInCurrent = currentArr.find((c) => c.label === item.label);
      if (!existsInCurrent) {
        const level = item.course_level || "Uncategorized";
        varianceMap[level] = (varianceMap[level] || 0) - item.y;
      }
    });

    return Object.entries(varianceMap).map(([label, y], index) => ({
      label,
      y,
      color: colors[index % colors.length],
    }));
  };

  // Socket Connection
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));

    const registerUser = () => {
      if (storedUser) {
        socket.emit("register", storedUser.user_name);
        setCurrentUserName(storedUser.user_name);
      }
    };

    socket.connect();
    registerUser();

    socket.on("connect", registerUser);

    const handleUsersUpdate = (users) => {
      setUsers(users);
    };

    socket.on("updateUsers", handleUsersUpdate);

    return () => {
      socket.off("updateUsers", handleUsersUpdate);
      socket.off("connect", registerUser);
    };
  }, []);

  const convertMergedData = (current, previous) => {
    const normalizeKey = (key) => (key || "").trim().toUpperCase();

    const currentMap = new Map();
    const previousMap = new Map();

    current.forEach((item) => {
      const key = normalizeKey(item.course_code);
      currentMap.set(key, {
        y: Number(item.course_count) || 0,
        course_level: item.course_level || "Uncategorized",
      });
    });

    previous.forEach((item) => {
      const key = normalizeKey(item.course_code);
      previousMap.set(key, {
        y: Number(item.course_count) || 0,
        course_level: item.course_level || "Uncategorized",
      });
    });

    const allLabels = new Set([...currentMap.keys(), ...previousMap.keys()]);

    const merged = Array.from(allLabels).map((label) => {
      const currentItem = currentMap.get(label);
      const previousItem = previousMap.get(label);

      return {
        label,
        current: currentItem?.y || 0,
        previous: previousItem?.y || 0,
        course_level:
          currentItem?.course_level ||
          previousItem?.course_level ||
          "Uncategorized",
      };
    });

    return merged;
  };

  const getGroupedTotalsByCourseLevel = () => {
    const grouped = {};

    classDataFull.current.forEach((item) => {
      const level = item.course_level?.trim() || "Uncategorized";
      grouped[level] = {
        level,
        current: (grouped[level]?.current || 0) + item.y,
        previous: grouped[level]?.previous || 0,
      };
    });

    classDataFull.previous.forEach((item) => {
      const level = item.course_level?.trim() || "Uncategorized";
      grouped[level] = {
        level,
        current: grouped[level]?.current || 0,
        previous: (grouped[level]?.previous || 0) + item.y,
      };
    });

    // Add variance field
    const groupedArray = Object.values(grouped).map((item) => ({
      ...item,
      variance: item.current - item.previous,
    }));

    // Sort alphabetically by course level
    return groupedArray.sort((a, b) => a.level.localeCompare(b.level));
  };

  // Frontend and Backend Integration
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API}/getClassSizeByCourse?qtr=${selectedQuarter}&year=${selectedYear}`
        );

        const { success, current, previous } = res.data;
        if (success) {
          const mergedData = convertMergedData(current || [], previous || []);

          // Sort by current descending
          const sorted = mergedData.sort((a, b) =>
            (a.course_level || "").localeCompare(b.course_level || "")
          );

          // Full data for modal (sorted)
          const fullCurrent = sorted.map((d) => ({
            label: d.label,
            y: d.current,
            course_level: d.course_level,
          }));

          const fullPrevious = sorted.map((d) => ({
            label: d.label,
            y: d.previous,
            course_level: d.course_level,
          }));

          // console.log("Merged Data (Q2):", mergedData);
          // console.log("Full Current (Q2):", fullCurrent);
          // console.log("Full Previous (Q2):", fullPrevious);

          setClassDataFull({
            current: fullCurrent,
            previous: fullPrevious,
          });

          setClassData({
            current: fullCurrent.slice(0, 10),
            previous: fullPrevious.slice(0, 10),
          });

          const varianceData = calculateVarianceByCourseLevel(
            fullCurrent,
            fullPrevious
          );
          setVarianceByLevel(varianceData);
        }
      } catch (err) {
        console.error("Failed to fetch class size data", err);
      }
    };

    const fetchHeadcountData = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API}/getHeadcountData?qtr=${selectedQuarter}&year=${selectedYear}`
        );

        const { success, current } = res.data;
        if (success) {
          const formattedData = (current || []).map((item) => ({
            label: item.course_code,
            y: Number(item.active_class) || 0,
            course_level: item.course_level || "Uncategorized",
          }));

          setHeadcountDataFull(formattedData);

          let totalActive = 0;
          let totalWithdrawn = 0;
          let totalDeferred = 0;

          current.forEach((item) => {
            totalActive += Number(item.active_class) || 0;
            totalWithdrawn += Number(item.withdrawn) || 0;
            totalDeferred += Number(item.deferred) || 0;
          });

          setTotals({
            active: totalActive,
            withdrawn: totalWithdrawn,
            deferred: totalDeferred,
          });
        }
      } catch (err) {
        console.error("Failed to fetch headcount data", err);
      }
    };

    fetchData();
    fetchHeadcountData();
  }, [selectedQuarter, selectedYear]);

  const [totals, setTotals] = useState({
    active: 0,
    withdrawn: 0,
    deferred: 0,
  });

  const handleToggleExpand = () => {
    setExpanded(!expanded);
  };

  const headerStyle = {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 10,
  };

  const chartBoxStyle = {
    backgroundColor: "#2f2f2f",
    borderRadius: 10,
    padding: 20,
    minWidth: 300,
    height: "300px",
    position: "relative",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    boxShadow:
      "inset 0 2px 6px rgba(0, 0, 0, 0.5), inset 0 -2px 4px rgba(255, 255, 255, 0.05)",
  };

  const iconStyle = {
    position: "absolute",
    top: 8,
    right: 15,
    color: "#bbb",
    cursor: "pointer",
  };

  const highLowBox = {
    backgroundColor: "#2C2C2C",
    borderRadius: 10,
    padding: 20,
    // flex: 1,
    boxShadow:
      "inset 0 2px 6px rgba(0, 0, 0, 0.5), inset 0 -2px 4px rgba(255, 255, 255, 0.05)",
  };

  const circleStyle = {
    backgroundColor: "#FFA726",
    color: "#000",
    fontWeight: "bold",
    borderRadius: "50%",
    width: 28,
    height: 28,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
  };

  return (
    <div
      style={{
        padding: "20px",
        backgroundColor: "#373636",
        margin: 20,
        borderRadius: 10,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <h5 style={{ color: "#fff", margin: 0 }}>Class Size</h5>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ display: "flex", gap: 8 }}>
            {users.map((user) => {
              return (
                <div
                  key={user.id}
                  title={user.name}
                  style={{
                    position: "relative", // Needed to position the green dot
                    width: 32,
                    height: 32,
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "50%",
                      backgroundColor: stringToColor(user.name),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12,
                      fontWeight: "bold",
                      color: "#fff",
                    }}
                  >
                    {user.name[0].toUpperCase()}
                  </div>

                  {user.name === currentUserName && isDrawingMode && (
                    <div
                      style={{
                        position: "absolute",
                        bottom: 0,
                        right: 0,
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: "#4CAF50",
                        border: "2px solid white",
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>

          <FaEdit
            size={20}
            style={{ color: "#fff", cursor: "pointer" }}
            onClick={() => setIsDrawingMode(!isDrawingMode)}
          />
          <FaExpandAlt
            size={20}
            onClick={handleToggleExpand}
            style={{ color: "#fff", cursor: "pointer" }}
            title={expanded ? "Collapse" : "Expand"}
          />
        </div>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: "20px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 20,
            flex: 1,
            minWidth: 250,
            height: "400px",
          }}
        >
          <div style={{ ...highLowBox, flex: 1 }}>
            <div style={{ color: "#aaa", fontSize: 14, marginBottom: 10 }}>
              Class Size (High - Low)
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <div
                  style={{ fontSize: 30, color: "#4CAF50", fontWeight: "bold" }}
                >
                  {maxClass ? maxClass.y : "-"}
                </div>
                <div style={{ fontSize: 12, color: "#ccc" }}>
                  {maxClass ? maxClass.label : "-"}
                </div>
              </div>

              <div style={{ textAlign: "center" }}>
                <div
                  style={{ fontSize: 30, color: "#F44336", fontWeight: "bold" }}
                >
                  {minClass ? minClass.y : "-"}
                </div>
                <div style={{ fontSize: 12, color: "#ccc" }}>
                  {minClass ? minClass.label : "-"}
                </div>
              </div>
            </div>
          </div>

          <div
            style={{
              ...highLowBox,
              minHeight: "465px",
              padding: "20px",
              display: "flex",
              flexDirection: "column",
              gap: "15px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span style={{ color: "#aaa", fontSize: 14 }}>
                Below Target Class Size
              </span>
              <span
                style={{
                  backgroundColor: "#555",
                  color: "#aaa",
                  fontWeight: "bold",
                  borderRadius: 20,
                  padding: "4px 12px",
                  fontSize: 13,
                  display: "inline-block",
                  minWidth: 28,
                  textAlign: "center",
                }}
              >
                {belowTargetClasses.length}
              </span>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 20,
                marginTop: "10px",
              }}
            >
              {paginatedClasses.length > 0 ? (
                paginatedClasses.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span style={{ color: "#fff", fontSize: 14 }}>
                      {item.label}
                    </span>
                    <span style={circleStyle}>{item.y}</span>
                  </div>
                ))
              ) : (
                <span style={{ color: "#aaa", fontSize: 13 }}>
                  No Class Data
                </span>
              )}

              {totalPages > 1 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 10,
                  }}
                >
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={currentPage === 1}
                    style={{
                      padding: "4px 10px",
                      borderRadius: 6,
                      border: "none",
                      backgroundColor: "#555",
                      color: "#fff",
                      cursor: "pointer",
                    }}
                  >
                    <IoIosArrowBack size={18} />
                  </button>
                  <span style={{ color: "#fff", fontSize: 12, marginTop: 8 }}>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                    style={{
                      padding: "4px 10px",
                      borderRadius: 6,
                      border: "none",
                      backgroundColor: "#555",
                      color: "#fff",
                      cursor: "pointer",
                    }}
                  >
                    <IoIosArrowForward size={18} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Column 2: Bar Charts */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 20,
            flex: 2,
            minWidth: 350,
          }}
        >
          <div style={chartBoxStyle}>
            <div style={iconStyle}>
              <div style={{ ...iconStyle, display: "flex", gap: 10 }}>
                <div title="View Class Size Chart">
                  <VscGraph size={18} onClick={() => setShowModal(true)} />
                </div>
                <div title="View Class Size Table">
                  <VscOpenPreview size={18} onClick={handleViewTable} />
                </div>
              </div>
            </div>

            <div style={headerStyle}>CLASS SIZE</div>
            <CanvasJSChart
              options={{
                height: 220,
                animationEnabled: true,
                backgroundColor: "#2f2f2f",
                axisX: {
                  labelFontColor: "#ccc",
                  title: "Courses",
                  titleFontColor: "#ccc",
                  labelAngle: -45,
                  labelFontSize: 9,
                },
                axisY: {
                  labelFontColor: "#ccc",
                  title: "Student Count",
                  titleFontColor: "#ccc",
                  interval: 10,
                },
                toolTip: { shared: true },
                legend: { cursor: "pointer", fontColor: "#fff" },
                data: [
                  {
                    type: "column",
                    name: `Q${selectedQuarter || 1} ${selectedYear}`,
                    showInLegend: true,
                    color: "#FFA500",
                    dataPoints: classData.current,
                    // indexLabel: "{y}",
                    // indexLabelFontColor: "#FFA500",
                    // indexLabelFontSize: 10,
                  },
                  {
                    type: "column",
                    name: `Q${
                      selectedQuarter === 1 ? 4 : selectedQuarter - 1
                    } ${
                      selectedQuarter === 1 ? selectedYear - 1 : selectedYear
                    }`,
                    showInLegend: true,
                    color: "#F2D092",
                    dataPoints: classData.previous,
                    // indexLabel: "{y}",
                    // indexLabelFontColor: "#F2D092",
                    // indexLabelFontSize: 10,
                  },
                ],
              }}
            />
          </div>

          <div style={chartBoxStyle}>
            <div style={headerStyle}>CLASS SIZE VARIANCE</div>
            <div style={{ display: "flex", gap: 20 }}>
              <div style={{ flex: 1 }}>
                <CanvasJSChart
                  options={{
                    height: 220,
                    animationEnabled: true,
                    backgroundColor: "#2f2f2f",
                    legend: { fontColor: "#fff" },
                    data: [
                      {
                        type: "doughnut",
                        indexLabel: "{label} ({y})",
                        indexLabelFontColor: "#fff",
                        dataPoints: varianceByLevel,
                      },
                    ],
                  }}
                />
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  backgroundColor: "#2f2f2f",
                  padding: 10,
                  borderRadius: 10,
                  minWidth: 150,
                  fontSize: 11,
                }}
              >
                <strong style={{ color: "#fff" }}>Course Levels</strong>
                {varianceByLevel.map((item, index) => (
                  <div
                    key={index}
                    style={{ display: "flex", alignItems: "center", gap: 10 }}
                  >
                    <div
                      style={{
                        width: 14,
                        height: 14,
                        backgroundColor: item.color,
                        borderRadius: 4,
                      }}
                    ></div>
                    <span style={{ color: "#fff" }}>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Column 3: Doughnut + Line Charts */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 20,
            flex: 2,
            minWidth: 350,
          }}
        >
          <div style={{ ...chartBoxStyle, height: "620px", overflowY: "auto" }}>
             <div style={iconStyle}>
              <div style={{ ...iconStyle, display: "flex", gap: 10 }}>
                <div title="View Class Detail">
                  <VscOpenPreview size={18} onClick={handleViewStatus} />
                </div>
              </div>
            </div>
            <div style={headerStyle} className="mb-4">
              CLASS SIZE BY COURSE LEVEL 
            </div>

            <div
              style={{
                display: "flex",
                gap: "16px",
                marginBottom: "16px",
                flexWrap: "wrap",
              }}
            >
              <div className="pill-dashboard">
                <div className="pill-circle">
                  <FaUser size={14} color="#000" />
                </div>
                <div className="pill-text">
                  <span className="pill-number">{totals.active}</span>
                  <span className="pill-label">Active</span>
                </div>
              </div>

              <div className="pill-dashboard">
                <div className="pill-circle">
                  <FaUserSlash size={16} color="#000" />
                </div>
                <div className="pill-text">
                  <span className="pill-number">{totals.withdrawn}</span>
                  <span className="pill-label">Withdrawn</span>
                </div>
              </div>

              <div className="pill-dashboard">
                <div className="pill-circle">
                  <FaUserXmark size={16} color="#000" />
                </div>
                <div className="pill-text">
                  <span className="pill-number">{totals.deferred}</span>
                  <span className="pill-label">Deferred</span>
                </div>
              </div>
            </div>

            {classDataFull.current.length > 0 ? (
              (() => {
                const data = getGroupedTotalsByCourseLevel();
                const totalCurrent = data.reduce(
                  (sum, item) => sum + item.current,
                  0
                );
                const totalPrevious = data.reduce(
                  (sum, item) => sum + item.previous,
                  0
                );
                const totalVariance = totalCurrent - totalPrevious;

                return (
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 13,
                      color: "#fff",
                      marginTop: "10px",
                    }}
                  >
                    <thead>
                      <tr style={{ backgroundColor: "#444" }}>
                        <th style={{ padding: "8px", textAlign: "left" }}>
                          COURSE LEVEL
                        </th>
                        <th style={{ padding: "8px", textAlign: "center" }}>
                          Q{selectedQuarter || 1} {selectedYear}
                        </th>
                        <th style={{ padding: "8px", textAlign: "center" }}>
                          Q{selectedQuarter === 1 ? 4 : selectedQuarter - 1}{" "}
                          {selectedQuarter === 1
                            ? selectedYear - 1
                            : selectedYear}
                        </th>
                        <th style={{ padding: "8px", textAlign: "center" }}>
                          VARIANCE
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.map((item, index) => (
                        <tr
                          key={index}
                          style={{
                            backgroundColor:
                              index % 2 === 0 ? "#333" : "#2b2b2b",
                          }}
                        >
                          <td style={{ padding: "8px" }}>{item.level}</td>
                          <td style={{ padding: "8px", textAlign: "center" }}>
                            {item.current}
                          </td>
                          <td style={{ padding: "8px", textAlign: "center" }}>
                            {item.previous}
                          </td>
                          <td
                            style={{
                              padding: "8px",
                              textAlign: "center",
                              color:
                                item.variance > 0
                                  ? "#4CAF50"
                                  : item.variance < 0
                                  ? "#F44336"
                                  : "#fff",
                              fontWeight: "bold",
                            }}
                          >
                            {item.variance > 0
                              ? `+${item.variance}`
                              : item.variance}
                          </td>
                        </tr>
                      ))}

                      {/* TOTAL ROW */}
                      <tr
                        style={{ backgroundColor: "#555", fontWeight: "bold" }}
                      >
                        <td style={{ padding: "8px" }}></td>
                        <td style={{ padding: "8px", textAlign: "center" }}>
                          {totalCurrent}
                        </td>
                        <td style={{ padding: "8px", textAlign: "center" }}>
                          {totalPrevious}
                        </td>
                        <td
                          style={{
                            padding: "8px",
                            textAlign: "center",
                            color:
                              totalVariance > 0
                                ? "#4CAF50"
                                : totalVariance < 0
                                ? "#F44336"
                                : "#fff",
                          }}
                        >
                          {totalVariance > 0
                            ? `+${totalVariance}`
                            : totalVariance}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                );
              })()
            ) : (
              <div style={{ color: "#aaa", fontSize: 14 }}>
                No class data available
              </div>
            )}
          </div>
        </div>
      </div>

      <GraphModal
        show={showModal}
        onClose={() => setShowModal(false)}
        title="CLASS SIZE COMPARISON"
        chartOptions={{
          height: 600,
          animationEnabled: true,
          backgroundColor: "#2f2f2f",
          axisX: {
            title: "Courses",
            labelFontColor: "#ccc",
            titleFontColor: "#ccc",
            reversed: true,
            interval: 1,
            labelFontSize: 12,
          },
          axisY: {
            title: "Student Count",
            labelFontColor: "#ccc",
            titleFontColor: "#ccc",
            gridColor: "#444",
            interval: 5,
          },
          toolTip: { shared: true },
          legend: {
            cursor: "pointer",
            fontColor: "#fff",
          },
          data: [
            {
              type: "bar",
              name: `Q${selectedQuarter === 1 ? 4 : selectedQuarter - 1} ${
                selectedQuarter === 1 ? selectedYear - 1 : selectedYear
              }`,
              showInLegend: true,
              color: "#F2D092",
              dataPoints: classDataFull.previous,
            },
            {
              type: "bar",
              name: `Q${selectedQuarter || 1} ${selectedYear}`,
              showInLegend: true,
              color: "#FFA500",
              dataPoints: classDataFull.current,
            },
          ],
        }}
      />

      {isDrawingMode && (
        <DrawingOverlay onClose={() => setIsDrawingMode(false)} />
      )}
    </div>
  );
}

export default ClassDashboard;
